<template>
  <div class="grouped-items-component" :class="embedded ? '' : 'container-fluid pt-2'">
 <div class="row">
        <div class="col-md-4 col-sm-12">
          <div class="text-left" :class="embedded ? 'pb-3' : 'py-3'">
            <h4 v-if="embedded" class="mfg-title">Grouped items</h4>
            <h3 v-else class="text-weight-900 text-primary">Grouped Inventory List</h3>
          </div>
        </div>

        <div class="col-md-8 col-sm-12">
          <div class="d-flex justify-content-end py-3">
            <button v-if="canMakeBatches && hasBatchItems" class="btn btn-outline-primary mr-2" @click="openBatchHistory(null)">
              <i class="bi bi-clock-history mr-1"></i> Batch history
            </button>
            <button class="btn btn-outline-secondary" :disabled="!items.length" @click="printListedBarcodes">
              <i class="bi bi-upc-scan mr-1"></i> Print Barcodes
            </button>
          </div>
        </div>
      </div>
    <!-- Top bar: Search + Create -->
    <div class="row mb-2 align-items-center">
      <div class="col-md-6 col-sm-12">
        <form @submit.prevent="getGroupedProducts" class="mb-0">
          <div class="input-group">
            <input
              type="search"
              class="form-control"
              v-model="searchItems"
              @input="onSearchInput"
              placeholder="Search grouped items..."
              aria-label="Search grouped items"
            />
            <div class="input-group-append">
              <button class="btn btn-outline-secondary" type="submit" title="Search">
                <i class="fa fa-search"></i>
              </button>
            </div>
          </div>
        </form>
      </div>

      <div class="col-md-6 col-sm-12 text-right">
        <button v-if="canEditRecipes" class="btn btn-outline-primary" @click="openCreateModal">
          <i class="fa fa-plus mr-1"></i> Create New Group Item
        </button>
      </div>
    </div>

    <!-- Results table -->
    <div class="mb-5">
      <b-table
        :items="items"
        :fields="fields"
        hover
        striped
        small
        responsive="sm"
        sticky-header="100vh"
        @row-clicked="onRowClick"
      >
        <template #cell(title)="data">
          <div class="d-flex flex-column">
            <strong class="text-primary">
              <i v-if="data.item.allOtherItems && data.item.allOtherItems.is_protected" class="bi bi-lock-fill text-dark mr-1" title="Protected with a PIN"></i>{{ data.item.title }}
            </strong>
            <small v-if="data.item.allOtherItems && data.item.allOtherItems.recipe_locked" class="text-muted">Protected — PIN needed to view</small>
            <small v-else class="text-muted">{{ data.item.items_count }} items</small>
          </div>
        </template>

        <template #cell(type)="data">
          <span
            v-if="data.item.allOtherItems && data.item.allOtherItems.type === 'manufactured'"
            class="badge badge-info"
            style="font-size:10px;"
          >Manufactured</span>
          <template v-else-if="data.item.type === 'batch'">
            <span class="badge badge-primary" style="font-size:10px;">Made in batches</span>
            <small v-if="data.item.allOtherItems.output_product" class="d-block text-secondary">
              {{ fmtQty(data.item.allOtherItems.output_product.remaining_units) }} {{ data.item.allOtherItems.output_product.product }} ready
            </small>
          </template>
          <span v-else class="badge badge-secondary" style="font-size:10px;">Bundle</span>
        </template>

        <template #cell(selling_price)="data">
          <div>{{ formatCurrency(data.item.selling_price) }}</div>
        </template>

        <template #cell(wholesale)="data">
          <div v-if="companySettings.has_wholesale">{{ formatCurrency(data.item.wholesale) }}</div>
          <div v-else class="text-muted">—</div>
        </template>
        <template #cell(actions)="row">
  <b-dropdown variant="link" size="sm" no-caret right boundary="window" :popper-opts="{ positionFixed: true }" @click.native.stop>
    <template #button-content>
      <i class="bi bi-three-dots-vertical"></i>
    </template>

    <template v-if="row.item.type === 'batch' && canMakeBatches">
      <b-dropdown-item @click="openMakeBatch(row.item)"><i class="bi bi-plus-circle mr-1"></i>Make batch</b-dropdown-item>
      <b-dropdown-item @click="openDiscard(row.item)"><i class="bi bi-trash mr-1"></i>Discard leftovers</b-dropdown-item>
      <b-dropdown-item @click="openBatchHistory(row.item)"><i class="bi bi-clock-history mr-1"></i>Batch history</b-dropdown-item>
      <b-dropdown-divider></b-dropdown-divider>
    </template>
    <b-dropdown-item v-if="canEditRecipes" @click="openEditModal(row.item)">Edit</b-dropdown-item>
    <a v-if="canEditRecipes" class="dropdown-item" href="#" @click="openAssignTaxesModal(row.item)">Assign Taxes & Levies</a>
    <a class="dropdown-item" href="#" @click.prevent="$refs.barcodeModal.open({ groupedIds: [row.item.id] })">Print Barcode</a>
    <a v-if="canManageInventory" class="dropdown-item" href="#" @click.prevent="generateBarcode(row.item)">Generate Barcode</a>
    <b-dropdown-item v-if="canEditRecipes" variant="danger" @click="deleteProd(row.item.id)">
      Delete
    </b-dropdown-item>
  </b-dropdown>
</template>
      </b-table>
    </div>

    <!-- VIEW DETAILS MODAL -->
    <b-modal ref="itemDetailsModal" hide-footer size="lg" title="Group Item Details">
      <div v-if="selectedProduct">
        <h5 class="mb-2">{{ selectedProduct.allOtherItems.group_title }}</h5>
        <div class="mb-3">
          <span class="mr-3"><strong>Retail:</strong> {{ formatCurrency(selectedProduct.allOtherItems.cost) }}</span>
          <span v-if="companySettings.has_wholesale"><strong>Wholesale:</strong> {{ formatCurrency(selectedProduct.allOtherItems.wholesale) }}</span>
        </div>

        <table class="table table-sm">
          <tbody>
            <tr>
              <td>Items in Group</td>
              <td>{{ (selectedProduct.allOtherItems.grouped_items || []).length }}</td>
            </tr>
            <tr>
              <td>Last Updated</td>
              <td>{{ formatDate(selectedProduct.allOtherItems.updated_at, 'hh:mm a DD-MM-YYYY') }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="selectedProduct.type === 'batch' && selectedProduct.allOtherItems.output_product" class="small text-secondary mb-2">
          {{ fmtQty(selectedProduct.allOtherItems.output_product.remaining_units) }} {{ selectedProduct.allOtherItems.output_product.product }} ready
        </div>

        <div class="d-flex justify-content-between align-items-center mt-3">
          <b-dropdown text="Actions" variant="outline-primary" boundary="window" :popper-opts="{ positionFixed: true }">
            <template v-if="selectedProduct.type === 'batch' && canMakeBatches">
              <b-dropdown-item @click="fromDetails(openMakeBatch)"><i class="bi bi-plus-circle mr-1"></i>Make batch</b-dropdown-item>
              <b-dropdown-item @click="fromDetails(openDiscard)"><i class="bi bi-trash mr-1"></i>Discard leftovers</b-dropdown-item>
              <b-dropdown-item @click="fromDetails(openBatchHistory)"><i class="bi bi-clock-history mr-1"></i>Batch history</b-dropdown-item>
              <b-dropdown-divider></b-dropdown-divider>
            </template>
            <b-dropdown-item v-if="canEditRecipes" @click="openEditModal(selectedProduct)">Edit</b-dropdown-item>
            <b-dropdown-item v-if="canEditRecipes" @click="fromDetails(openAssignTaxesModal)">Assign Taxes &amp; Levies</b-dropdown-item>
            <b-dropdown-item @click="fromDetails(i => $refs.barcodeModal.open({ groupedIds: [i.id] }))">Print Barcode</b-dropdown-item>
            <b-dropdown-item v-if="canManageInventory" @click="fromDetails(generateBarcode)">Generate Barcode</b-dropdown-item>
            <b-dropdown-item v-if="canEditRecipes" variant="danger" @click="deleteProd(selectedProduct.allOtherItems.id)">Delete</b-dropdown-item>
          </b-dropdown>
          <button class="btn btn-secondary" @click="$refs.itemDetailsModal.hide()">Close</button>
        </div>

      </div>
      <div v-else class="text-center">Loading...</div>
    </b-modal>

    <!-- EDIT / CREATE MODAL (same form used for create & edit) -->
    <b-modal ref="groupModal" size="lg" :title="isEditing ? `Edit ${form.group_title}` : 'Create Group Item'" hide-footer>
      <form @submit.prevent="submitGroupForm" novalidate>
        <div class="form-row">
          <div class="form-group col-md-8">
            <label class="mb-1">Group Title</label>
            <input class="form-control" v-model="form.group_title" required placeholder="e.g. Breakfast Combo" />
          </div>

          <div class="form-group col-md-4">
            <label class="mb-1">Status</label>
            <select class="form-control" v-model="form.is_active">
              <option :value="true">Active</option>
              <option :value="false">Inactive</option>
            </select>
          </div>
        </div>

        <!-- MANUFACTURING ADDITION: type selector -->
        <div class="form-row">
          <div class="form-group col-md-4">
            <label class="mb-1">Type</label>
            <select class="form-control" v-model="form.type">
              <option value="bundle">Bundle — made as it's sold</option>
              <option value="batch">Prepared in batches — made ahead (chips, mandazi)</option>
              <option v-if="companySettings.has_manufacturing || form.type === 'manufactured'" value="manufactured">Manufactured — production orders</option>
            </select>
            <small class="text-muted">
              <span v-if="form.type === 'bundle'">Each sale takes the ingredients off stock.</span>
              <span v-else-if="form.type === 'batch'">Use “Make batch” to cook a batch: the ingredients come off stock and the finished product goes on. The POS sells the finished product.</span>
              <span v-else>POS availability = physical produced stock of the output product.</span>
            </small>
          </div>

          <div class="form-group col-md-8" v-if="form.type === 'manufactured' || form.type === 'batch'">
            <label class="mb-1">{{ form.type === 'batch' ? 'Finished product' : 'Output Product' }} <span class="text-danger">*</span></label>

            <div class="position-relative">
              <input
                type="text"
                class="form-control"
                v-model="form.output_product_search"
                @input="onOutputProductSearch"
                :placeholder="form.output_product_id ? form.output_product_name : 'Search product...'"
                autocomplete="off"
              />
                     <small class="text-muted d-block mb-1">{{ form.type === 'batch' ? 'What one portion of the batch is, e.g. “Chips” — the POS sells it at its own price. Ingredient amounts below are per portion.' : 'The finished product that production creates and POS sells' }}</small>
              <div
                v-if="(form.output_product_suggestions && form.output_product_suggestions.length) || canOfferNewOutput"
                class="dropdown-menu show w-100"
                style="z-index: 2500;"
              >
                <button
                  v-for="p in form.output_product_suggestions"
                  :key="p.id"
                  type="button"
                  class="dropdown-item d-flex justify-content-between"
                  @mousedown.prevent="selectOutputProduct(p)"
                >
                  <span>{{ p.product }}</span>
                  <small class="text-muted">{{ p.remaining_units }} in stock</small>
                </button>
                <div v-if="canOfferNewOutput && form.output_product_suggestions && form.output_product_suggestions.length" class="dropdown-divider"></div>
                <button v-if="canOfferNewOutput" type="button" class="dropdown-item text-primary" @mousedown.prevent="startNewOutput">
                  <i class="bi bi-plus-lg mr-1"></i>Create “{{ form.output_product_search.trim() }}” as a new product
                </button>
              </div>
            </div>

            <!-- Quick-create the finished product without leaving this form -->
            <div v-if="newOutput.open" class="border rounded p-3 mt-2 bg-white">
              <div class="font-weight-bold mb-2" style="font-size:13px;">New product</div>
              <div class="form-row">
                <div class="form-group col-md-6 mb-2">
                  <label class="mb-1 small">Name</label>
                  <input class="form-control" v-model="newOutput.product" />
                </div>
                <div class="form-group col-md-6 mb-2">
                  <label class="mb-1 small">Category</label>
                  <select class="custom-select" v-model="newOutput.productcat_id">
                    <option value="">Choose…</option>
                    <option v-for="c in outputCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                </div>
                <div class="form-group col-md-4 mb-2">
                  <label class="mb-1 small">Unit</label>
                  <input class="form-control" v-model="newOutput.measuring_unit" placeholder="e.g. pcs, kg" />
                </div>
                <div class="form-group col-md-4 mb-2">
                  <label class="mb-1 small">Selling price</label>
                  <input type="number" min="0" step="any" class="form-control" v-model.number="newOutput.ppu" />
                </div>
                <div class="form-group col-md-4 mb-2 d-flex align-items-end">
                  <div class="custom-control custom-checkbox">
                    <input type="checkbox" class="custom-control-input" id="newOutputSold" v-model="newOutput.can_be_sold" />
                    <label class="custom-control-label small" for="newOutputSold">Sold to customers</label>
                  </div>
                </div>
              </div>
              <small class="text-muted d-block mb-2">Untick “Sold to customers” for something you only use to make other products — like dough for chapati and maandazi.</small>
              <div v-if="newOutput.error" class="text-danger small mb-2">{{ newOutput.error }}</div>
              <button type="button" class="btn btn-primary btn-sm" :disabled="newOutput.saving || !newOutput.product || !newOutput.productcat_id" @click="saveNewOutput">Create product</button>
              <button type="button" class="btn btn-outline-secondary btn-sm ml-1" @click="newOutput.open = false">Cancel</button>
            </div>
            <small v-if="form.output_product_id" class="text-success">
              <i class="bi bi-check-circle mr-1"></i>{{ form.output_product_name }}
            </small>
          </div>
        </div>
        <!-- END MANUFACTURING ADDITION -->

        <div v-if="form.type === 'batch'" class="alert alert-secondary py-2">
          The POS sells the finished product at its own price (set it on the product in Inventory).
        </div>
        <div v-else class="form-row">
          <div class="form-group col-md-4">
            <label class="mb-1">Selling Price</label>
            <input type="number" step="any" class="form-control" v-model.number="form.cost" placeholder="e.g. 1500" />
          </div>

          <div class="form-group col-md-4">
            <label class="mb-1">Wholesale Price</label>
            <input type="number" step="any" class="form-control" v-model.number="form.wholesale" placeholder="e.g. 1200" />
          </div>

          <div class="form-group col-md-4">
            <label class="mb-1">VAT Status</label>
            <select class="form-control" v-model="form.vat_category">
              <option value="standard">Has VAT (16%)</option>
              <option value="zero">Zero Rated</option>
              <option value="exempted">Exempted</option>
            </select>
          </div>
        </div>

        <hr />

        <!-- Add items typeahead -->
        <div class="form-group">
          <label >Add Item</label>
          <div class="position-relative">
            <input
              type="text"
              class="form-control"
              v-model="inventorySearch.term"
              @input="onInventoryType"
              @keydown.down.prevent="focusNextSuggestion"
              @keydown.up.prevent="focusPrevSuggestion"
              @keydown.enter.prevent="selectHighlightedSuggestion"
              placeholder="Type item name to add..."
              autocomplete="off"
            />
            <!-- suggestions dropdown -->
            <div v-if="inventorySearch.show && inventorySearch.suggestions.length" class="dropdown-menu show w-100" style="z-index: 2000;">
              <button
                v-for="(s, idx) in inventorySearch.suggestions"
                :key="s.id"
                type="button"
                class="dropdown-item d-flex justify-content-between align-items-center"
                :class="{ active: inventorySearch.highlightIndex === idx }"
                @mousedown.prevent="addInventoryItemFromSuggestion(s)"
              >
                <div>
                  <div>{{ s.product }}</div>
                  <small class="text-muted d-block">{{ s.sku || '' }}</small>
                </div>
                <small :class="stockBadgeClass(s.remaining_units)">{{ s.remaining_units }} left</small>
              </button>
            </div>
          </div>
        </div>

        <!-- Selected items list -->
        <div v-if="form.items.length" class="mb-3">
          <div class="list-group">
            <div v-if="itm.product" v-for="(itm, idx) in form.items" :key="itm.temp_uid || itm.id" class="list-group-item">
              <div class="d-flex align-items-start">
                <div class="mr-3" style="width: 42%">
                  <strong  class="d-block text-capitalize">{{ itm.product.product }}</strong>
                  <small class="text-muted">{{ itm.product.sku || '' }}</small>
                </div>

                <div class="mr-3 d-flex flex-column" style="width: 28%">
                  <label class="mb-1">Qty</label>
                  <input type="number" min="0" step="any" class="form-control form-control-sm" v-model.number="itm.count" />
                  <small class="text-muted mt-1">Remaining: <span :class="stockBadgeClass(itm.product.remaining_units)">{{ itm.product.remaining_units }}</span></small>
                </div>

                <div class="mr-3" style="width: 20%">
                  <label class="mb-1">Required?</label>
                  <div>
                    <div class="custom-control custom-switch">
                      <input type="checkbox" class="custom-control-input" :id="'req_'+idx" v-model="itm.is_required" />
                      <label class="custom-control-label" :for="'req_'+idx">{{ itm.is_required ? 'Yes' : 'No' }}</label>
                    </div>
                  </div>
                </div>

                <div class="ml-auto text-right">
                  <button class="btn btn-outline-secondary btn-sm mb-2" @click.prevent="toggleSubstitutes(itm)">{{ itm.showSubstitutes ? 'Hide' : 'Substitutes' }}</button>
                  <button class="btn btn-danger btn-sm" @click.prevent="removeItemFromForm(idx)"><i class="bi bi-x"></i></button>
                </div>
              </div>

              <!-- Substitutes area -->
              <div v-if="itm.showSubstitutes" class="mt-3 pl-4">

                <div v-for="(sub, sIdx) in itm.substitutes" :key="sub.temp_uid || sub.id" class="d-flex align-items-center mb-2">

                    <input type="text"
                         class="form-control form-control-sm mr-2"
                         v-model="sub.name"
                         @input="onSubstituteType(itm, sub, sIdx)"
                         placeholder="Search substitute..."
                         style="flex:1" />
                  <input type="number" min="0" step="any" class="form-control form-control-sm mr-2" v-model.number="sub.count" style="width:90px" />
                  <select v-model.number="sub.priority" class="form-control form-control-sm mr-2" style="width:110px">
                    <option v-for="n in 5" :key="n" :value="n">Priority {{ n }}</option>
                  </select>
                  <button class="btn btn-sm btn-outline-danger" @click.prevent="removeSubstitute(itm, sIdx)"><i class="bi bi-x"></i></button>

                  <!-- substitute suggestions dropdown -->
                  <div v-if="sub.showDropdown && sub.suggestions && sub.suggestions.length" class="dropdown-menu show" style="position:absolute; z-index:2100; max-height:220px; overflow:auto;">
                    <button v-for="opt in sub.suggestions" :key="opt.id" class="dropdown-item" @mousedown.prevent="selectSubstitute(itm, sub, opt)">
                      {{ opt.product }} <small class="text-muted d-block">{{ opt.sku || '' }}</small>
                    </button>
                  </div>
                </div>

                <div>
                  <button class="btn btn-sm btn-outline-primary" @click.prevent="addSubstitute(itm)" :disabled="(itm.substitutes||[]).length >= 5">
                    + Add Substitute
                  </button>
                </div>
              </div>
            </div>
            <div v-else>

                <p class="text-danger">An item in this list was deleted</p>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="text-center text-muted mb-3">No items added to this group yet. Use the search box above to add.</div>

        <!-- Settings area -->
        <hr />
        <div class="form-row align-items-center">
          <div class="form-group col-md-6">
            <label class="mb-1">Allow group sale if required items missing?</label>
            <select class="form-control" v-model="form.settings.allow_missing_required">
              <option :value="false">No — Block sale</option>
              <option :value="true">Yes — Allow sale</option>
            </select>
          </div>

          <div class="form-group col-md-6">
            <label class="mb-1">Deduction strategy</label>
            <select class="form-control" v-model="form.settings.deduction_strategy">
              <option value="original_first">Original first, then substitutes</option>
              <option value="substitute_first">Use substitutes first</option>
              <option value="most_stock">Use the item with most stock</option>
            </select>
          </div>
        </div>

        <!-- PIN protection (proprietary recipes). Checked and enforced server-side (RecipeLock). -->
        <hr />
        <div v-if="!isEditing">
          <div class="custom-control custom-switch mb-2">
            <input id="protectRecipe" v-model="form.protect" type="checkbox" class="custom-control-input">
            <label class="custom-control-label" for="protectRecipe"><i class="bi bi-lock mr-1"></i>Protect this recipe with a PIN</label>
          </div>
          <div v-if="form.protect" class="form-row">
            <div class="form-group col-md-6">
              <input v-model="form.protect_pin" type="password" inputmode="numeric" maxlength="8" autocomplete="new-password" class="form-control" placeholder="PIN (4–8 digits)">
            </div>
            <div class="form-group col-md-6">
              <input v-model="form.protect_pin_confirmation" type="password" inputmode="numeric" maxlength="8" autocomplete="new-password" class="form-control" placeholder="Confirm PIN">
            </div>
            <small class="col-12 text-muted mb-2">Everyone, including admins, will see only the title until they enter this PIN. Only you can change it. If it is lost, only Dukakit support can reset it.</small>
          </div>
        </div>
        <div v-else-if="protection.loaded && (protection.is_protected || protection.can_manage)">
          <p class="mb-2">
            <i class="bi mr-1" :class="protection.is_protected ? 'bi-lock-fill' : 'bi-unlock'"></i>
            <strong>{{ protection.is_protected ? 'Protected with a PIN' : 'Not protected' }}</strong>
            <small v-if="protection.is_protected && !protection.is_pin_owner" class="text-muted d-block">Only the person who set the PIN can change it.</small>
          </p>
          <template v-if="protection.can_manage">
            <div class="form-row">
              <div v-if="protection.is_protected" class="form-group col-md-4">
                <input v-model="protection.current_pin" type="password" inputmode="numeric" maxlength="8" autocomplete="off" class="form-control" placeholder="Current PIN">
              </div>
              <div class="form-group" :class="protection.is_protected ? 'col-md-4' : 'col-md-6'">
                <input v-model="protection.pin" type="password" inputmode="numeric" maxlength="8" autocomplete="new-password" class="form-control" :placeholder="protection.is_protected ? 'New PIN' : 'PIN (4–8 digits)'">
              </div>
              <div class="form-group" :class="protection.is_protected ? 'col-md-4' : 'col-md-6'">
                <input v-model="protection.pin_confirmation" type="password" inputmode="numeric" maxlength="8" autocomplete="new-password" class="form-control" placeholder="Confirm PIN">
              </div>
            </div>
            <div class="d-flex" style="gap:8px;">
              <button type="button" class="btn btn-outline-primary" :disabled="protection.busy" @click="savePin">{{ protection.is_protected ? 'Change PIN' : 'Protect with PIN' }}</button>
              <button v-if="protection.is_protected" type="button" class="btn btn-outline-danger" :disabled="protection.busy" @click="removePin">Remove protection</button>
            </div>
          </template>
        </div>

        <!-- Actions -->
        <div class="d-flex justify-content-between mt-3">
          <div>
            <button type="button" class="btn btn-outline-secondary" @click="resetForm">Reset</button>
            <button v-if="isEditing && canEditRecipes" type="button" class="btn btn-danger ml-2" @click="deleteProd(form.id)">Delete Group</button>
          </div>
          <div>
            <button type="button" class="btn btn-secondary mr-2" @click="$refs.groupModal.hide()">Cancel</button>
            <button v-if="canEditRecipes" type="submit" class="btn btn-primary">{{ isEditing ? 'Save Changes' : 'Create Group' }}</button>
          </div>
        </div>
      </form>
    </b-modal>
          <b-modal id="assign-taxes-modal" hide-footer>
  <h6 class="py-2">
    You can assign applicable taxes to this item
  </h6>

  <p>
    <small>
      Can't see the tax or levy you need?
      <button type="button" class="btn btn-link p-0 align-baseline" @click="showTaxModal = true">Create one</button>
    </small>
  </p>

  <form v-if="taxes.length" @submit.prevent="assignTaxesToItem">
    <div
      v-for="tax in taxes"
      :key="tax.id"
      class="form-check"
    >
      <input
        class="form-check-input"
        type="checkbox"
        :value="tax.id"
        v-model="selectedTaxes"
        :id="'tax-' + tax.id"
      />

      <label
        class="form-check-label"
        :for="'tax-' + tax.id"
      >
        {{ tax.tax }} ({{ tax.tax_perc }}%)
      </label>
    </div>

    <div class="form-group mt-3">
      <button type="submit" class="btn btn-primary">
        Submit
      </button>
    </div>
  </form>

  <div v-else class="d-flex justify-content-center">
    <div class="spinner-grow" role="status">
      <span class="sr-only">Loading...</span>
    </div>
  </div>
</b-modal>

<!-- MAKE BATCH -->
<b-modal ref="makeBatchModal" :title="batch.item ? 'Make batch: ' + batch.item.title : 'Make batch'" hide-footer>
  <div v-if="batch.item">
    <div class="form-group">
      <label class="mb-1">How many {{ batchOutputName }} were made?</label>
      <input v-model.number="batch.quantity" type="number" min="0" step="any" class="form-control" placeholder="e.g. 20" autofocus>
      <small v-if="batchOutput" class="text-secondary">{{ fmtQty(batchOutput.remaining_units) }} ready now.</small>
    </div>
    <div v-if="batch.quantity > 0 && batch.item.items.length" class="mb-3">
      <div class="small text-secondary mb-1">Comes off stock</div>
      <ul class="list-unstyled mb-0">
        <li v-for="it in batch.item.items" :key="it.id" class="d-flex justify-content-between">
          <span>{{ ingredientOf(it) ? ingredientOf(it).product : 'Item' }}</span>
          <span :class="ingredientOf(it) && ingredientOf(it).remaining_units < it.count * batch.quantity ? 'text-danger' : 'text-dark'">
            {{ fmtQty(it.count * batch.quantity) }}<template v-if="ingredientOf(it)"> of {{ fmtQty(ingredientOf(it).remaining_units) }}</template>
          </span>
        </li>
      </ul>
    </div>
    <div class="form-group">
      <label class="mb-1">Note <span class="text-secondary">(optional)</span></label>
      <input v-model="batch.note" type="text" maxlength="255" class="form-control" placeholder="e.g. Lunch rush">
    </div>
    <div v-if="batch.error" class="alert alert-danger py-2">{{ batch.error }}</div>
    <div class="d-flex justify-content-end">
      <button class="btn btn-secondary mr-2" @click="$refs.makeBatchModal.hide()">Cancel</button>
      <button class="btn btn-primary" :disabled="batch.busy || !(batch.quantity > 0)" @click="saveBatch">
        <span v-if="batch.busy" class="spinner-border spinner-border-sm mr-1"></span>Record batch
      </button>
    </div>
  </div>
</b-modal>

<!-- DISCARD LEFTOVERS -->
<b-modal ref="discardModal" :title="batch.item ? 'Discard leftovers: ' + batch.item.title : 'Discard leftovers'" hide-footer>
  <div v-if="batch.item">
    <div class="form-group">
      <label class="mb-1">How many {{ batchOutputName }} are being thrown away?</label>
      <input v-model.number="batch.quantity" type="number" min="0" step="any" class="form-control">
      <small v-if="batchOutput" class="text-secondary">{{ fmtQty(batchOutput.remaining_units) }} ready now.</small>
    </div>
    <div class="form-group">
      <label class="mb-1">Reason</label>
      <input v-model="batch.note" type="text" maxlength="255" class="form-control" placeholder="e.g. End of day, gone cold">
    </div>
    <div v-if="batch.error" class="alert alert-danger py-2">{{ batch.error }}</div>
    <div class="d-flex justify-content-end">
      <button class="btn btn-secondary mr-2" @click="$refs.discardModal.hide()">Cancel</button>
      <button class="btn btn-danger" :disabled="batch.busy || !(batch.quantity > 0) || (batch.note || '').trim().length < 3" @click="saveDiscard">
        <span v-if="batch.busy" class="spinner-border spinner-border-sm mr-1"></span>Discard
      </button>
    </div>
  </div>
</b-modal>

<!-- BATCH HISTORY -->
<b-modal ref="batchHistoryModal" :title="history.item ? 'Batch history: ' + history.item.title : 'Batch history'" size="lg" hide-footer>
  <div v-if="history.loading" class="text-center py-3"><span class="spinner-border text-primary"></span></div>
  <div v-else-if="!history.rows.length" class="text-center text-secondary py-3">No batches yet.</div>
  <div v-else>
    <div v-for="b in history.rows" :key="b.id" class="border-bottom py-2">
      <div class="d-flex justify-content-between align-items-start">
        <div>
          <span class="badge mr-1" :class="b.kind === 'make' ? 'badge-primary' : 'badge-danger'">{{ b.kind === 'make' ? 'Made' : 'Discarded' }}</span>
          <strong :class="b.undone_at ? 'text-secondary' : 'text-dark'" :style="b.undone_at ? 'text-decoration:line-through' : ''">{{ fmtQty(b.quantity) }} {{ b.output }}</strong>
          <span v-if="!history.item" class="text-secondary"> · {{ b.title }}</span>
          <div class="small text-secondary">
            {{ formatDate(b.at, 'DD MMM, h:mm a') }}<template v-if="b.by"> · {{ b.by }}</template>
            <template v-if="b.total_cost"> · cost {{ formatCurrency(b.total_cost) }}</template>
            <template v-if="b.note"> · {{ b.note }}</template>
          </div>
          <div v-if="b.kind === 'make' && b.ingredients.length" class="small text-secondary">
            Used: {{ b.ingredients.map(i => fmtQty(i.quantity) + ' ' + i.name + (i.substitute ? ' (substitute)' : '')).join(', ') }}
          </div>
          <div v-if="b.undone_at" class="small text-danger">Undone {{ formatDate(b.undone_at, 'h:mm a') }}<template v-if="b.undone_by"> by {{ b.undone_by }}</template></div>
        </div>
        <button v-if="b.can_undo" class="btn btn-outline-danger btn-sm" :disabled="history.undoing === b.id" @click="askUndo(b)">Undo</button>
      </div>
    </div>
    <div v-if="history.lastPage > 1" class="d-flex justify-content-between align-items-center mt-2">
      <button class="btn btn-outline-secondary btn-sm" :disabled="history.page <= 1" @click="loadHistory(history.page - 1)">Previous</button>
      <small class="text-secondary">Page {{ history.page }} of {{ history.lastPage }}</small>
      <button class="btn btn-outline-secondary btn-sm" :disabled="history.page >= history.lastPage" @click="loadHistory(history.page + 1)">Next</button>
    </div>
  </div>
</b-modal>

<b-modal ref="undoBatchModal" title="Undo" ok-title="Undo" ok-variant="danger" cancel-variant="secondary" @ok="undoBatch">
  <p v-if="history.confirm" class="mb-0">
    <template v-if="history.confirm.kind === 'make'">Undo this batch of {{ fmtQty(history.confirm.quantity) }} {{ history.confirm.output }}? The ingredients go back on stock and the {{ history.confirm.output }} come off.</template>
    <template v-else>Put the {{ fmtQty(history.confirm.quantity) }} discarded {{ history.confirm.output }} back on stock?</template>
  </p>
</b-modal>

<tax-quick-add-modal v-if="showTaxModal" @close="showTaxModal = false" @created="onTaxCreated"></tax-quick-add-modal>

    <barcode-labels-modal ref="barcodeModal" :can-generate="canManageInventory" @generated="getGroupedProducts"></barcode-labels-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';
import TaxQuickAddModal from '../quotesInvoices/TaxQuickAddModal.vue';
import moment from 'moment';
import debounce from 'lodash/debounce';
import BarcodeLabelsModal from './BarcodeLabelsModal.vue';

export default {
  name: 'GroupedItemsManager',
  props: {
    // Rendered inside the Manufacturing hub instead of its own page.
    embedded: { type: Boolean, default: false },
  },
  components: { TaxQuickAddModal, BarcodeLabelsModal },
  data() {
    return {
      // main table state
      searchItems: '',
      taxes: [],
      selectedTaxes: [],
      showTaxModal: false,
      items: [],
      fields: [
        { key: 'title', label: 'Title', sortable: true },
        { key: 'type', label: 'Type', sortable: false },
        { key: 'items_count', label: 'Items', sortable: true },
        { key: 'selling_price', label: 'Retail', sortable: true },
        { key: 'wholesale', label: 'Wholesale', sortable: true },
        { key: 'actions', label: '' }
      ],

      // settings & user
      companySettings: {},
      userRole: '',
      selectedProduct: null,

      // inventory search/state
      inventoryItemsAll: [], // loaded once
      newOutput: { open: false, product: '', productcat_id: '', measuring_unit: '', ppu: 0, can_be_sold: true, saving: false, error: null },
      outputCategories: [],
      inventorySearch: {
        term: '',
        show: false,
        suggestions: [],
        highlightIndex: 0
      },

      // form for creating/updating group
      form: this.emptyForm(),
      protection: this.emptyProtection(),

      // convenience for determining mode
      isEditing: false,

      // "Prepared in batches": make / discard / history (BatchPrepService)
      batch: { item: null, quantity: null, note: '', busy: false, error: '' },
      history: { item: null, rows: [], page: 1, lastPage: 1, loading: false, undoing: null, confirm: null }
    };
  },

  computed: {
    /** Offer "create new product" when the typed name doesn't exactly match an existing one. */
    canOfferNewOutput() {
      const q = (this.form.output_product_search || '').trim().toLowerCase();
      if (q.length < 2) return false;
      if (this.form.output_product_id && q === (this.form.output_product_name || '').trim().toLowerCase()) return false;
      return !this.inventoryItemsAll.some(p => (p.product || '').trim().toLowerCase() === q);
    },
    ...mapGetters(['userDetails']),
    canManageInventory() {
      // keep same permission function you used
      if (!this.userDetails) return false;
      return this.userDetails.roles && this.userDetails.roles[0] && this.userDetails.roles[0].name === 'Admin'
        || this.userDetails.permissions && (this.userDetails?.permissions || []).some(p => p.name === 'can manage inventory');
    },
    // Matches the /api/group-item write routes.
    canEditRecipes() {
      return this.hasPermission('edit-recipes');
    },
    // Matches the /api/batches routes (Admin gets every permission in /api/user).
    canMakeBatches() {
      return this.hasPermission('make-batches');
    },
    hasBatchItems() {
      return this.items.some(i => i.type === 'batch');
    },
    batchOutput() {
      return this.batch.item && this.batch.item.allOtherItems.output_product;
    },
    batchOutputName() {
      return this.batchOutput ? this.batchOutput.product : 'portions';
    }
  },

  watch: {
    // keep a local debounced loader for search box
    searchItems(val) {
      // nothing immediate — debounce does the job via onSearchInput
    }
  },

  mounted() {
    this.getCompanySettings();
    this.getProducts(); // loads all inventory items for search/substitutes
    this.getGroupedProducts();
    this.getUserDetails();

    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
        if(modalId === 'assign-taxes-modal')
            {
              this.getTaxes();
            }
    })
  },

  methods: {
    // ---------- UTILITIES ----------
    emptyProtection() {
      return { loaded: false, is_protected: false, can_manage: false, is_pin_owner: false, current_pin: '', pin: '', pin_confirmation: '', busy: false };
    },

    async loadProtection(id) {
      this.protection = this.emptyProtection();
      try {
        const { data } = await axios.get(`/api/recipes/${id}/pin-status`);
        this.protection = { ...this.emptyProtection(), ...data, loaded: true };
      } catch (e) { /* no access to manage: section stays hidden */ }
    },

    pinError(e) {
      const d = e.response && e.response.data;
      return d && d.errors ? Object.values(d.errors).flat().join(' ') : ((d && d.message) || 'Request failed');
    },

    async savePin() {
      this.protection.busy = true;
      try {
        const { data } = await axios.put(`/api/recipes/${this.form.id}/pin`, {
          current_pin: this.protection.current_pin || undefined,
          pin: this.protection.pin,
          pin_confirmation: this.protection.pin_confirmation,
        });
        this.$toasts.push({ type: 'success', message: data.message });
        await this.loadProtection(this.form.id);
        this.getGroupedProducts();
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: this.pinError(e) });
      } finally {
        this.protection.busy = false;
      }
    },

    async removePin() {
      this.protection.busy = true;
      try {
        const { data } = await axios.delete(`/api/recipes/${this.form.id}/pin`, { data: { current_pin: this.protection.current_pin } });
        this.$toasts.push({ type: 'success', message: data.message });
        await this.loadProtection(this.form.id);
        this.getGroupedProducts();
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: this.pinError(e) });
      } finally {
        this.protection.busy = false;
      }
    },

    /**
     * A protected recipe arrives in the list without its items. Fetch it in
     * full — the server asks for the PIN first (RecipePinPrompt) — or return
     * false if the PIN was not given.
     */
    async loadFullItem(item) {
      const other = item && item.allOtherItems;
      if (!other || !other.recipe_locked) return true;
      try {
        const { data } = await axios.get(`/api/group-item/${other.id}`);
        item.allOtherItems = data;
        item.items = data.grouped_items || [];
        item.items_count = item.items.length;
        return true;
      } catch (e) {
        return false;
      }
    },

    emptyForm() {
      return {
        protect: false,
        protect_pin: '',
        protect_pin_confirmation: '',
        id: null,
        group_title: '',
        cost: null,
        wholesale: null,
        vat_category: 'standard',
        is_active: true,
        type: 'bundle',
        output_product_id: null,
        output_product_name: '',
        output_product_search: '',
        output_product_suggestions: [],
        items: [],
        settings: {
          allow_missing_required: false,
          deduction_strategy: 'original_first'
        }
      };
    },

    fmtQty(n) {
      const v = Number(n || 0);
      return Number.isInteger(v) ? String(v) : v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
    },

    /** Run a row action from the details modal: close it first so modals don't stack. */
    fromDetails(action) {
      const item = this.selectedProduct;
      this.$refs.itemDetailsModal.hide();
      this.$nextTick(() => action(item));
    },

    ingredientOf(it) {
      return it.product || this.inventoryItemsAll.find(p => p.id === it.product_id) || null;
    },

    errorText(e) {
      const d = e.response && e.response.data;
      return d && d.errors ? Object.values(d.errors).flat().join(' ') : ((d && d.message) || 'Request failed');
    },

    // No PIN prompt: making a batch doesn't need the recipe; a protected one just shows no breakdown.
    openMakeBatch(item) {
      this.batch = { item, quantity: null, note: '', busy: false, error: '' };
      this.$refs.makeBatchModal.show();
    },

    openDiscard(item) {
      this.batch = { item, quantity: null, note: '', busy: false, error: '' };
      this.$refs.discardModal.show();
    },

    async saveBatch() {
      this.batch.busy = true;
      this.batch.error = '';
      try {
        const { data } = await axios.post(`/api/batches/items/${this.batch.item.id}/make`, { quantity: this.batch.quantity, note: this.batch.note || null });
        this.$toasts.push({ type: 'success', message: data.message });
        this.$refs.makeBatchModal.hide();
        this.getGroupedProducts();
      } catch (e) {
        this.batch.error = this.errorText(e);
      } finally {
        this.batch.busy = false;
      }
    },

    async saveDiscard() {
      this.batch.busy = true;
      this.batch.error = '';
      try {
        const { data } = await axios.post(`/api/batches/items/${this.batch.item.id}/discard`, { quantity: this.batch.quantity, reason: this.batch.note });
        this.$toasts.push({ type: 'success', message: data.message });
        this.$refs.discardModal.hide();
        this.getGroupedProducts();
      } catch (e) {
        this.batch.error = this.errorText(e);
      } finally {
        this.batch.busy = false;
      }
    },

    openBatchHistory(item) {
      this.history = { item, rows: [], page: 1, lastPage: 1, loading: false, undoing: null, confirm: null };
      this.$refs.batchHistoryModal.show();
      this.loadHistory(1);
    },

    async loadHistory(page) {
      this.history.loading = true;
      try {
        const { data } = await axios.get('/api/batches/history', {
          params: { page, grouped_inventory_id: this.history.item ? this.history.item.id : undefined },
        });
        this.history.rows = data.data;
        this.history.page = data.meta.current_page;
        this.history.lastPage = data.meta.last_page;
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: this.errorText(e) });
      } finally {
        this.history.loading = false;
      }
    },

    askUndo(b) {
      this.history.confirm = b;
      this.$refs.undoBatchModal.show();
    },

    async undoBatch() {
      const b = this.history.confirm;
      if (!b) return;
      this.history.undoing = b.id;
      try {
        const { data } = await axios.post(`/api/batches/${b.id}/undo`);
        this.$toasts.push({ type: 'success', message: data.message });
        this.loadHistory(this.history.page);
        this.getGroupedProducts();
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: this.errorText(e) });
      } finally {
        this.history.undoing = null;
      }
    },

    printListedBarcodes() {
      this.$refs.barcodeModal.open({ groupedIds: this.items.map(i => i.id) });
    },
    generateBarcode(item) {
      this.$refs.barcodeModal.generateItem({
        groupedId: item.id,
        name: item.title,
        currentSku: item.allOtherItems && item.allOtherItems.sku,
      });
    },
    formatCurrency(val) {
      if (val === null || val === undefined) return 'KES 0.00';
      const n = parseFloat(val) || 0;
      return `KES ${n.toFixed(2)}`;
    },

    formatDate(date, fmt = 'DD-MM-YYYY') {
      return date ? moment(date).format(fmt) : '-';
    },

    stockBadgeClass(units) {
      if (units === null || units === undefined) return 'text-muted';
    //   if (units <= 0) return 'text-danger';
    //   if (units <= 5) return 'text-dark';
      return 'text-dark';
    },

    // ---------- API loaders ----------
    async getCompanySettings() {
      try {
        const res = await axios.get('/api/settings');
        this.companySettings = res.data || {};
      } catch (e) { /* silent */ }
    },
    openAssignTaxesModal(item) {
    this.selectedProduct = item;   // store row.item
    this.$bvModal.show('assign-taxes-modal');
  },

    async getProducts() {
      // loads all inventory items used for typeahead & substitutes
      try {
        const res = await axios.get('/api/all-items-and-count');
        this.inventoryItemsAll = res.data.items || [];
      } catch (e) { console.error(e); }
    },

    async getUserDetails() {
      try {
        const res = await axios.get('/api/user');
        this.userRole = res.data.roles && res.data.roles[0] ? res.data.roles[0].name : '';
        // userDetails in Vuex should remain available
      } catch (e) { /* silent */ }
    },

async getGroupedProducts() {
  try {
    const res = await axios.get('/api/grouped-inventory', {
      params: { search: this.searchItems, with_output_product: 1 }
    });

    const payload = Array.isArray(res.data) ? res.data : [];

    const arr = payload.map(value => {
      if (!value || typeof value !== 'object') return null;

      let settings = value.settings;

      // ✅ Ensure settings is always an object
      if (typeof settings === 'string') {
        try {
          settings = JSON.parse(settings);
        } catch (err) {
          console.warn('Failed to parse settings, using defaults:', err);
          settings = {
            allow_missing_required: false,
            deduction_strategy: 'original_first'
          };
        }
      }

      // If it's null or not an object, set default
      if (!settings || typeof settings !== 'object') {
        settings = {
          allow_missing_required: false,
          deduction_strategy: 'original_first'
        };
      }

      return {
        id: value.id,
        title: value.group_title,
        type: value.type || 'bundle',
        items: value.grouped_items || [],
        items_count: (value.grouped_items || []).length,
        wholesale: value.wholesale,
        selling_price: value.cost,
        settings, // ✅ now always an object
        allOtherItems: value
      };
    }).filter(Boolean); // Remove nulls

    this.items = arr;
  } catch (e) {
    console.error('Error fetching grouped products:', e);
  }
},

    // ---------- Search (debounced) ----------
    onSearchInput: debounce(function () {
      this.getGroupedProducts();
    }, 350),

    // ---------- Row click ----------
    async onRowClick(item) {
      if (!item) return;
      if (!(await this.loadFullItem(item))) return;
      this.selectedProduct = item;
      // show details modal
      this.$refs.itemDetailsModal.show();
    },

    // ---------- Create/Edit flows ----------
    openCreateModal() {
      this.isEditing = false;
      this.form = this.emptyForm();
      this.$refs.groupModal.show();
    },

  async openEditModal(itemDetails) {
    if (!(await this.loadFullItem(itemDetails || this.selectedProduct))) return;

    let other = null
    if(itemDetails)
    {
          other = itemDetails.allOtherItems;
    } else{
        other = this.selectedProduct.allOtherItems
    }


  // ✅ Ensure settings is always an object
  let settings = other.settings;
  if (typeof settings === 'string') {
    try {
      settings = JSON.parse(settings);
    } catch (err) {
      console.warn('Failed to parse settings in openEditModal:', err);
      settings = {
        allow_missing_required: false,
        deduction_strategy: 'original_first'
      };
    }
  }

  if (!settings || typeof settings !== 'object') {
    settings = {
      allow_missing_required: false,
      deduction_strategy: 'original_first'
    };
  }

  this.isEditing = true;
  this.form = {
    id: other.id,
    group_title: other.group_title,
    cost: other.cost,
    wholesale: other.wholesale,
    vat_category: other.vat_category || 'standard',
    is_active: other.is_active !== undefined ? other.is_active : true,
    type: other.type || 'bundle',
    output_product_id: other.output_product_id || null,
    output_product_name: other.output_product ? other.output_product.product : '',
    output_product_search: other.output_product ? other.output_product.product : '',
    output_product_suggestions: [],
    items: (other.grouped_items || [])
  .filter(it => it && (it.product || it.product_id)) // allow fallback
  .map(it => {
    // 🔥 normalize product
    let productObj = null;

    if (it.product && it.product.id) {
      productObj = it.product;
    } else if (it.product_id) {
      // fallback: find from inventory list
      const found = this.inventoryItemsAll.find(p => p.id === it.product_id);
      if (found) productObj = found;
    }

    // 🚫 skip completely if still invalid
    if (!productObj) return null;

    return {
      id: it.id,
      product: productObj,
      count: it.count || 1,
      is_required: it.is_required !== undefined ? it.is_required : true,
      substitutes: (it.substitutes || [])
        .map(s => {
          let subProduct = null;

          if (s.product && s.product.id) {
            subProduct = s.product;
          } else if (s.product_id) {
            subProduct = this.inventoryItemsAll.find(p => p.id === s.product_id);
          }

          if (!subProduct) return null;

          return {
            id: subProduct.id,
            name: subProduct.product,
            count: s.count || 1,
            priority: s.priority || 1,
            temp_uid: s.id ? null : this._tempUid()
          };
        })
        .filter(Boolean), // ✅ remove bad substitutes
      showSubstitutes: false,
      temp_uid: it.id ? null : this._tempUid()
    };
  })
  .filter(Boolean), // ✅ remove bad items completely
    settings // ✅ Always an object now
  };

  this.loadProtection(other.id);
  this.$refs.groupModal.show();
},

    // small uid for temporary items (frontend only)
    _tempUid() {
      return `t_${Math.random().toString(36).slice(2, 9)}`;
    },

    // ---------- Inventory typeahead & selection ----------
    onInventoryType: debounce(function () {
      const term = (this.inventorySearch.term || '').trim().toLowerCase();
      if (!term) {
        this.inventorySearch.show = false;
        this.inventorySearch.suggestions = [];
        return;
      }
      // local filter - could change to server-side search if dataset is big
      const suggestions = this.inventoryItemsAll.filter(it => (it.product || '').toLowerCase().includes(term));
      this.inventorySearch.suggestions = suggestions.slice(0, 12);
      this.inventorySearch.show = suggestions.length > 0;
      this.inventorySearch.highlightIndex = 0;
    }, 200),

    focusNextSuggestion() {
      if (!this.inventorySearch.show) return;
      const max = this.inventorySearch.suggestions.length - 1;
      this.inventorySearch.highlightIndex = Math.min(max, this.inventorySearch.highlightIndex + 1);
    },

    focusPrevSuggestion() {
      if (!this.inventorySearch.show) return;
      this.inventorySearch.highlightIndex = Math.max(0, this.inventorySearch.highlightIndex - 1);
    },

    selectHighlightedSuggestion() {
      const sel = this.inventorySearch.suggestions[this.inventorySearch.highlightIndex];
      if (sel) this.addInventoryItemFromSuggestion(sel);
    },

    addInventoryItemFromSuggestion(item) {
      // push item into form.items (avoid duplicates)
      const exists = this.form.items.some(i => i.product && i.product.id === item.id);
      if (!exists) {
        this.form.items.push({
          id: null, // group-unit id (if editing existing)
          product: Object.assign({}, item),
          count: 1,
          is_required: true,
          substitutes: [],
          showSubstitutes: false,
          temp_uid: this._tempUid()
        });
      } else {
        // bump count visually
        const found = this.form.items.find(i => i.product.id === item.id);
        found.count = (found.count || 0) + 1;
      }
      this.inventorySearch.term = '';
      this.inventorySearch.show = false;
      this.inventorySearch.suggestions = [];
    },

    // ---------- Form item management ----------
    removeItemFromForm(index) {
      this.form.items.splice(index, 1);
    },

    toggleSubstitutes(item) {
      this.$set(item, 'showSubstitutes', !item.showSubstitutes);
      if (!item.substitutes) this.$set(item, 'substitutes', []);
    },

    addSubstitute(item) {
      if (!item.substitutes) this.$set(item, 'substitutes', []);
      if (item.substitutes.length >= 5) return;
      item.substitutes.push({
        id: null,
        name: '',
        count: 1,
        priority: item.substitutes.length + 1,
        suggestions: [],
        showDropdown: false,
        temp_uid: this._tempUid()
      });
    },

    removeSubstitute(item, idx) {
      item.substitutes.splice(idx, 1);
    },

    onSubstituteType(item, sub, sIdx) {
      const q = (sub.name || '').trim().toLowerCase();
      sub.showDropdown = !!q;
      if (!q) {
        sub.suggestions = [];
        return;
      }
      // filter from inventory list
      sub.suggestions = this.inventoryItemsAll.filter(it => (it.product||'').toLowerCase().includes(q)).slice(0, 10);
    },

    selectSubstitute(item, sub, opt) {
      sub.name = opt.product;
      sub.id = opt.id;
      sub.showDropdown = false;
      sub.suggestions = [];
    },

    // ---------- Submit create / edit ----------
    async submitGroupForm() {
      // Basic validation
      if (!this.form.group_title || !this.form.items.length) {
        this.$toasts.push({ type: 'danger', message: 'Provide a group title and at least one item.' });
        return;
      }

      // MANUFACTURING ADDITION: manufactured type requires an output product
      if ((this.form.type === 'manufactured' || this.form.type === 'batch') && !this.form.output_product_id) {
        this.$toasts.push({ type: 'danger', message: this.form.type === 'batch' ? 'Choose the finished product the batch makes.' : 'Select an output product for a manufactured item.' });
        return;
      }

      // Prepare payload consistent with your backend expectations
      const payload = {
        ...this.form,
        type: this.form.type || 'bundle',
        output_product_id: (this.form.type === 'manufactured' || this.form.type === 'batch') ? this.form.output_product_id : null,
        // Batch items sell as their finished product; the group's own price is unused.
        cost: this.form.type === 'batch' ? (this.form.cost || 0) : this.form.cost,
        grouped_items: this.form.items
            .filter(it => it && it.product && it.product.id)
            .map(it => ({
                id: it.id || null,
                product_id: it.product.id,
                count: it.count,
                is_required: !!it.is_required,
                substitutes: (it.substitutes || [])
                .filter(s => s && s.id)
                .map(s => ({
                    product_id: s.id,
                    product_name: s.name,
                    count: s.count,
                    priority: s.priority
                }))
            }))
      };

      delete payload.protect;
      if (this.isEditing || !this.form.protect) {
        delete payload.protect_pin;
        delete payload.protect_pin_confirmation;
      }

      try {
        if (this.isEditing) {
          await axios.put('/api/group-item', payload);
          this.$toasts.push({ type: 'success', message: 'Grouped Item updated successfully' });
        } else {
          await axios.post('/api/group-item', payload);
          this.$toasts.push({ type: 'success', message: 'Grouped Item created successfully' });
        }
        this.$refs.groupModal.hide();
        this.getGroupedProducts();
      } catch (err) {
        console.error(err);
        const d = err.response && err.response.data;
        const msg = d && d.errors ? Object.values(d.errors).flat().join(' ') : ((d && d.message) || 'Request failed');
        this.$toasts.push({ type: 'danger', message: msg });
      }
    },

    // ---------- Delete endpoints ----------
    async deleteProd(item_id) {
      const ok = await this.$bvModal.msgBoxConfirm('Delete this grouped item? This cannot be undone.', {
        title: 'Delete grouped item', okTitle: 'Delete', okVariant: 'danger', cancelVariant: 'secondary', centered: true,
      });
      if (!ok) return;
      try {
        await axios.delete('/api/group-item', { params: { id: item_id } });
        this.$toasts.push({ type: 'success', message: 'Grouped Item deleted' });
        this.$refs.groupModal.hide();
        this.$refs.itemDetailsModal.hide();
        this.getGroupedProducts();
      } catch (e) {
        console.error(e);
        this.$toasts.push({ type: 'danger', message: 'Delete failed' });
      }
    },

    async deleteGroupedItem(groupitem_id) {
      try {
        await axios.delete('/api/group-item-unit', { params: { id: groupitem_id } });
        this.$toasts.push({ type: 'success', message: 'Grouped item removed' });
        // mutate local selectedProduct to reflect deletion
        if (this.selectedProduct && this.selectedProduct.allOtherItems) {
          const filtered = (this.selectedProduct.allOtherItems.grouped_items || []).filter(i => i.id !== groupitem_id);
          this.selectedProduct.allOtherItems.grouped_items = filtered;
        }
      } catch (e) {
        console.error(e);
        this.$toasts.push({ type: 'danger', message: 'Failed to remove grouped item' });
      }
    },

//     openAssignTaxesModal(item) {


//   // product.more_details.taxes = [1,2,3]
//   this.selectedTaxes = item?.more_details?.taxes
//     ? [...item.more_details.taxes]
//     : [];

//   this.getTaxes();

// },

    async getTaxes() {

            return await axios.get('/api/taxes')
                .then(({ data }) => {
                    this.taxes = data.data.map(t => ({
                        id: t.id,
                        tax: t.tax,
                        tax_perc: Number(t.tax_perc),
                        enabled: !!t.enabled,
                        on_receipt: !!t.on_receipt
                    }));
                })
                .catch(err => console.error('getTaxes', err))
                .finally();
        },

    async onTaxCreated(tax) {
            this.showTaxModal = false;
            await this.getTaxes();
            if (!this.selectedTaxes.includes(tax.id)) {
                this.selectedTaxes.push(tax.id);
            }
        },

    async assignTaxesToItem() {
            try {
                await axios.post(
                `/api/assign-taxes-grouped-item/${this.selectedProduct.id}`,
                { taxes: this.selectedTaxes }
                );

                this.$toasts?.push({
                type: 'success',
                message: 'Taxes assigned successfully'
                });

                this.$bvModal.hide('assign-taxes-modal');
            } catch (err) {
                console.error(err);
                this.$toasts?.push({
                type: 'error',
                message: 'Failed to assign taxes'
                });
            }
        },

    // ---------- MANUFACTURING ADDITION: output product search ----------
    async onOutputProductSearch() {
      const q = (this.form.output_product_search || '').toLowerCase().trim();
      if (q.length < 2) {
        this.$set(this.form, 'output_product_suggestions', []);
        return;
      }
      const results = this.inventoryItemsAll
        .filter(p => (p.product || '').toLowerCase().includes(q))
        .slice(0, 10);
      this.$set(this.form, 'output_product_suggestions', results);
    },

    async startNewOutput() {
      this.newOutput = { open: true, product: this.form.output_product_search.trim(), productcat_id: '', measuring_unit: '', ppu: 0, can_be_sold: true, saving: false, error: null };
      this.$set(this.form, 'output_product_suggestions', []);
      if (!this.outputCategories.length) {
        try { this.outputCategories = (await axios.get('/api/group-item/output-product-categories')).data; } catch (e) { this.outputCategories = []; }
      }
    },

    async saveNewOutput() {
      this.newOutput.saving = true; this.newOutput.error = null;
      try {
        const { data } = await axios.post('/api/group-item/output-product', {
          product: this.newOutput.product, productcat_id: this.newOutput.productcat_id,
          measuring_unit: this.newOutput.measuring_unit || null, ppu: this.newOutput.ppu || 0, can_be_sold: this.newOutput.can_be_sold,
        });
        this.inventoryItemsAll.push(data);
        this.selectOutputProduct(data);
        this.newOutput.open = false;
        this.$toasts.push({ type: 'success', message: `${data.product} created` });
      } catch (e) {
        const d = e.response && e.response.data;
        this.newOutput.error = d && d.errors ? Object.values(d.errors).flat().join(' ') : ((d && d.message) || 'Could not create the product.');
      } finally { this.newOutput.saving = false; }
    },

    selectOutputProduct(product) {
      this.$set(this.form, 'output_product_id', product.id);
      this.$set(this.form, 'output_product_name', product.product);
      this.$set(this.form, 'output_product_search', product.product);
      this.$set(this.form, 'output_product_suggestions', []);
    },
    // ---------- END MANUFACTURING ADDITION ----------

    resetForm() {
      this.form = this.emptyForm();
      this.isEditing = false;
    },

    // ---------- small keyboard helpers ----------
    addInventoryItemFromSuggestionShortcut(index) {
      const s = this.inventorySearch.suggestions[index];
      if (s) this.addInventoryItemFromSuggestion(s);
    }
  }
};
</script>

<style scoped>
.grouped-items-component .dropdown-menu.show { max-height: 300px; overflow:auto; }
.list-group-item { background: #fff; }
.stock-badge { font-weight: 600; }
.dropdown-item.active, .dropdown-item:active {
  color: #000;
  text-decoration: none;
  background-color: #fd47196f;
}
</style>
